import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

const initialState = {
  drpClientFeedbacks: [],
  currentDrpClientFeedback: null,
  isLoading: false,
  error: null,
};

export const fetchDrpClientFeedbacks = createAsyncThunk(
  'drpClientFeedback/fetchAll',
  async (patientId, { rejectWithValue }) => {
    try {
      const url = patientId ? `/drp-client-feedbacks/${patientId}` : '/drp-client-feedbacks';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch DRP Client Feedback');
    }
  }
);

export const fetchDrpClientFeedback = createAsyncThunk(
  'drpClientFeedback/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/drp-client-feedbacks/show/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch DRP Client Feedback');
    }
  }
);

export const createDrpClientFeedback = createAsyncThunk(
  'drpClientFeedback/create',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await api.post('/drp-client-feedbacks', formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to create DRP Client Feedback');
    }
  }
);

export const updateDrpClientFeedback = createAsyncThunk(
  'drpClientFeedback/update',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/drp-client-feedbacks/${id}`, formData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(error.response?.data?.error || 'Failed to update DRP Client Feedback');
    }
  }
);

export const deleteDrpClientFeedback = createAsyncThunk(
  'drpClientFeedback/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/drp-client-feedbacks/${id}`);
      return { id, message: response.data.message };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete DRP Client Feedback');
    }
  }
);

const slice = createSlice({
  name: 'drpClientFeedback',
  initialState,
  reducers: {
    clearCurrentDrpClientFeedback: (state) => { state.currentDrpClientFeedback = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrpClientFeedbacks.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchDrpClientFeedbacks.fulfilled, (s, a) => { s.isLoading = false; s.drpClientFeedbacks = a.payload; })
      .addCase(fetchDrpClientFeedbacks.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(fetchDrpClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(fetchDrpClientFeedback.fulfilled, (s, a) => { s.isLoading = false; s.currentDrpClientFeedback = a.payload; })
      .addCase(fetchDrpClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(createDrpClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(createDrpClientFeedback.fulfilled, (s, a) => { s.isLoading = false; s.drpClientFeedbacks.unshift(a.payload); })
      .addCase(createDrpClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(updateDrpClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(updateDrpClientFeedback.fulfilled, (s, a) => {
        s.isLoading = false;
        s.currentDrpClientFeedback = a.payload;
        const i = s.drpClientFeedbacks.findIndex((r) => r.id === a.payload.id);
        if (i !== -1) s.drpClientFeedbacks[i] = a.payload;
      })
      .addCase(updateDrpClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; })
      .addCase(deleteDrpClientFeedback.pending, (s) => { s.isLoading = true; s.error = null; })
      .addCase(deleteDrpClientFeedback.fulfilled, (s, a) => { s.isLoading = false; s.drpClientFeedbacks = s.drpClientFeedbacks.filter((r) => r.id !== a.payload.id); })
      .addCase(deleteDrpClientFeedback.rejected, (s, a) => { s.isLoading = false; s.error = a.payload; });
  },
});

export const { clearCurrentDrpClientFeedback, clearError } = slice.actions;
export default slice.reducer;
